"use client"

import * as React from 'react'
import { injectionWidgetEntries } from '@/.mercato/generated/injection-widgets.generated'
// Side-effect: registers translatable fields for client-side TranslationManager
import '@/.mercato/generated/translations-fields.generated'
import { injectionTables } from '@/.mercato/generated/injection-tables.generated'
import { registerCoreInjectionWidgets, registerCoreInjectionTables } from '@open-mercato/core/modules/widgets/lib/injection'
import { registerInjectionWidgets } from '@open-mercato/ui/backend/injection/widgetRegistry'
import { dashboardWidgetEntries } from '@/.mercato/generated/dashboard-widgets.generated'
import { registerDashboardWidgets } from '@open-mercato/ui/backend/dashboard/widgetRegistry'
import { notificationHandlerEntries } from '@/.mercato/generated/notification-handlers.generated'
import { registerNotificationHandlers } from '@open-mercato/shared/lib/notifications/handler-registry'
// Side-effect: registers translatable fields for client-side TranslationManager
import '@/.mercato/generated/translations-fields.generated'
// Side-effect: configures message UI component and object type registries on the client.
import '@/.mercato/generated/messages.client.generated'

let _clientBootstrapped = false
let _serviceWorkerRegistration: Promise<ServiceWorkerRegistration | null> | null = null

function registerDriverServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null)
  }
  if (_serviceWorkerRegistration) return _serviceWorkerRegistration
  _serviceWorkerRegistration = navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .catch((error) => {
      console.warn('[mercato] failed to register driver service worker', error)
      return null
    })
  return _serviceWorkerRegistration
}

function clientBootstrap() {
  if (_clientBootstrapped) return
  _clientBootstrapped = true

  // Register injection widgets
  registerInjectionWidgets(injectionWidgetEntries)
  registerCoreInjectionWidgets(injectionWidgetEntries)
  registerCoreInjectionTables(injectionTables)

  // Register dashboard widgets
  registerDashboardWidgets(dashboardWidgetEntries)

  // Register notification handlers for client-side reactive effects
  registerNotificationHandlers(notificationHandlerEntries)
}

async function requestDriverQueueReplay() {
  const registration = await registerDriverServiceWorker()
  registration?.active?.postMessage({ type: 'OM_DRIVER_REPLAY_QUEUE' })
}

export function ClientBootstrapProvider({ children }: { children: React.ReactNode }) {
  React.useEffect(() => {
    clientBootstrap()
    void registerDriverServiceWorker()

    const handleOnline = () => {
      void requestDriverQueueReplay()
    }

    window.addEventListener('online', handleOnline)
    return () => {
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  // Also bootstrap synchronously on first render for SSR hydration
  if (typeof window !== 'undefined' && !_clientBootstrapped) {
    clientBootstrap()
  }

  return <>{children}</>
}
